/**
 * LocalStorageWorkspaceAdapter - Browser localStorage implementation
 * Stores workspace presets and repository states in browser localStorage
 */

import type {
  WorkspaceLayout,
  WorkspaceStorageAdapter,
  RepositoryWorkspaceState,
} from '../types/workspace.types';

export class LocalStorageWorkspaceAdapter implements WorkspaceStorageAdapter {
  private readonly PRESETS_KEY = 'panel-layouts:workspace-presets';
  private readonly REPO_STATE_PREFIX = 'panel-layouts:repo-state:';

  /**
   * Load all user-created workspace presets
   */
  async loadWorkspacePresets(): Promise<Record<string, WorkspaceLayout>> {
    try {
      const stored = localStorage.getItem(this.PRESETS_KEY);
      if (!stored) {
        return {};
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to load workspace presets:', error);
      return {};
    }
  }

  /**
   * Save workspace presets
   */
  async saveWorkspacePresets(
    presets: Record<string, WorkspaceLayout>,
  ): Promise<void> {
    try {
      localStorage.setItem(this.PRESETS_KEY, JSON.stringify(presets));
    } catch (error) {
      console.error('Failed to save workspace presets:', error);
      throw error;
    }
  }

  /**
   * Load repository-specific workspace state
   */
  async loadRepositoryState(
    repositoryKey: string,
  ): Promise<RepositoryWorkspaceState | null> {
    try {
      const key = `${this.REPO_STATE_PREFIX}${repositoryKey}`;
      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error(
        `Failed to load repository state for ${repositoryKey}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Save repository-specific workspace state
   */
  async saveRepositoryState(
    repositoryKey: string,
    state: RepositoryWorkspaceState,
  ): Promise<void> {
    try {
      const key = `${this.REPO_STATE_PREFIX}${repositoryKey}`;
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(
        `Failed to save repository state for ${repositoryKey}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Load all repository states
   */
  async loadAllRepositoryStates(): Promise<
    Record<string, RepositoryWorkspaceState>
  > {
    try {
      const allStates: Record<string, RepositoryWorkspaceState> = {};

      // Iterate through all localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.REPO_STATE_PREFIX)) {
          const repositoryKey = key.substring(this.REPO_STATE_PREFIX.length);
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              allStates[repositoryKey] = JSON.parse(stored);
            } catch (parseError) {
              console.error(
                `Failed to parse repository state for ${repositoryKey}:`,
                parseError,
              );
            }
          }
        }
      }

      return allStates;
    } catch (error) {
      console.error('Failed to load all repository states:', error);
      return {};
    }
  }
}
